import { round } from 'lodash-es';
import { Graphics, Point, settings, Sprite, } from 'pixi.js-legacy';
import Constants from '../constants';
import { ScalableTextManager, ScalableTextOptions, SelectManager, SelectOptions, TooltipManager, TooltipOptions, TooltipPosition, } from '../managers';
import ViewStyle from '../viewStyle';
export class BeamOptions {
    constructor() {
        this.id = '';
        this.direction = Constants.vertical;
        this.startPosition = new Point(0, 0);
        this.endPosition = new Point(0, 0);
        this.mark = '0';
        this.chosenSection = '';
        this.viewStyle = ViewStyle.Diagram;
        this.constructionType = 'Inset';
    }
}
export class Beam extends Sprite {
    constructor(pixiRenderer, options = new BeamOptions()) {
        super();
        this.beamScale = 2;
        this.beamResolution = 10;
        const width = options.direction === Constants.horizontal
            ? options.endPosition.x - options.startPosition.x
            : 8;
        const height = options.direction === Constants.horizontal
            ? 8
            : options.endPosition.y - options.startPosition.y;
        this.beamRenderer = pixiRenderer;
        this.viewStyle = options.viewStyle;
        this.chosenSection = options.chosenSection;
        this.name = 'Beam';
        this.id = options.id;
        this.tier = options.tier;
        this.mark = options.mark;
        this.direction = options.direction;
        this.startPosition = options.startPosition;
        this.endPosition = options.endPosition;
        this.constructionType = options.constructionType;
        this.width = width;
        this.height = height;
        if (options.viewStyle === ViewStyle.Diagram) {
            this.diagramMode();
        }
        else {
            this.designMode();
        }
        if (options.direction === Constants.horizontal) {
            this.anchor.x = 0;
            this.anchor.y = 0.5;
        }
        else {
            this.anchor.x = 0.5;
            this.anchor.y = 0;
        }
        this.position.x = options.startPosition.x;
        this.position.y = options.startPosition.y;
        this.direction === Constants.horizontal
            ? this.horizontalMark()
            : this.verticalMark();
        const selectableOptions = new SelectOptions();
        selectableOptions.select = this.select.bind(this);
        selectableOptions.deselect = this.deselect.bind(this);
        SelectManager.getInstance().addSelectable(this, selectableOptions);
        const tooltipOptions = new TooltipOptions();
        tooltipOptions.text =
            `Beam (${this.mark}) \n` +
                `X: ${round(this.x / Constants.scalingFactor, 3)}, ` +
                `Y: ${round(this.y / Constants.scalingFactor, 3)}\nSection: ${this.chosenSection}`;
        tooltipOptions.position =
            this.direction === Constants.horizontal
                ? TooltipPosition.Top
                : TooltipPosition.Right;
        tooltipOptions.tierOffset = this.tier.position;
        TooltipManager.getInstance().addTooltip(this, tooltipOptions);
    }
    deselect() {
        if (this.viewStyle === ViewStyle.Diagram) {
            this.diagramMode();
        }
        else {
            this.designMode();
        }
    }
    select() {
        if (this.viewStyle === ViewStyle.Diagram) {
            this.diagramMode(true);
        }
        else {
            this.designMode(true);
        }
    }
    diagramMode(selected = false) {
        const graphics = new Graphics();
        this.viewStyle = ViewStyle.Diagram;
        graphics.lineStyle(4 * this.beamScale, selected ? 0xff0000 : 0x000000);
        if (this.constructionType === 'Standard') {
            this.drawStandard(graphics);
        }
        else {
            if (this.direction === Constants.horizontal) {
                graphics.moveTo(0, 0);
                graphics.lineTo(this.width, 0);
            }
            else {
                graphics.moveTo(0, 0);
                graphics.lineTo(0, this.height);
            }
        }
        this.texture.destroy();
        this.texture = this.beamRenderer.generateTexture(graphics, {
            scaleMode: settings.SCALE_MODE,
            resolution: this.beamResolution,
        });
        graphics.destroy();
    }
    designMode(selected = false) {
        const graphics = new Graphics();
        this.viewStyle = ViewStyle.Design;
        if (this.constructionType === 'Standard') {
            graphics.lineStyle(4 * this.beamScale, selected ? 0x8aacb8 : 0xadd8e6);
            this.drawStandard(graphics);
            const offset = this.direction === Constants.horizontal
                ? this.height * this.beamScale
                : this.width * this.beamScale;
            this.drawStandard(graphics, offset);
        }
        else {
            graphics.lineStyle(4 * this.beamScale, selected ? 0x8aacb8 : 0xadd8e6);
            graphics.beginFill(0x0000ff, 0);
            graphics.drawRect(0, 0, this.width * this.beamScale, this.height * this.beamScale);
            graphics.endFill();
        }
        this.texture.destroy();
        this.texture = this.beamRenderer.generateTexture(graphics, {
            scaleMode: settings.SCALE_MODE,
            resolution: this.beamResolution,
        });
        graphics.destroy();
    }
    verticalMark() {
        const txtOptions = new ScalableTextOptions();
        txtOptions.text = this.mark;
        txtOptions.x = this.position.x - 10;
        txtOptions.y = this.position.y + this.height / 2;
        txtOptions.size = 40;
        txtOptions.anchor = new Point(0.5, 0.5);
        ScalableTextManager.getInstance().createText(this.tier.marks, txtOptions);
    }
    horizontalMark() {
        const txtOptions = new ScalableTextOptions();
        txtOptions.text = this.mark;
        txtOptions.x = this.position.x + this.width / 2;
        txtOptions.y = this.position.y + 10;
        txtOptions.size = 40;
        txtOptions.anchor = new Point(0.5, 0.5);
        ScalableTextManager.getInstance().createText(this.tier.marks, txtOptions);
    }
    drawStandard(graphics, offset = 0) {
        const dashLength = 36;
        let sX = this.startPosition.x;
        let sY = this.startPosition.y;
        let eX = this.endPosition.x;
        let eY = this.endPosition.y;
        if (this.direction === Constants.horizontal) {
            sY += offset;
            eY += offset;
        }
        else {
            sX += offset;
            eX += offset;
        }
        graphics.moveTo(sX, sY);
        const dX = eX - sX;
        const dY = eY - sY;
        const dashes = Math.floor(Math.sqrt(dX * dX + dY * dY) / dashLength);
        const dashX = dX / dashes;
        const dashY = dY / dashes;
        let q = 0;
        while (q++ < dashes) {
            sX += dashX;
            sY += dashY;
            if (q % 2 === 0) {
                graphics.moveTo(sX, sY);
            }
            else {
                graphics.lineTo(sX, sY);
            }
        }
        if (q % 2 === 0) {
            graphics.moveTo(sX, sY);
        }
        else {
            graphics.lineTo(sX, sY);
        }
    }
}
